import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { arApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, Loader2, Download, Pencil, Trash2, History, FileText, Filter, ArrowDownCircle, ArrowUpCircle, Link2, Unlink, AlertTriangle, MessageCircle, Send, Copy, CheckCircle2, Landmark, Receipt,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import ReceivePaymentDialog from './ReceivePaymentDialog';
import { useAuth } from '../context/AuthContext';

function fmtINR(n) {
  if (n == null || isNaN(n)) return '\u20B90';
  const sign = n < 0 ? '-' : '';
  const num = Math.round(Math.abs(n) * 100) / 100;
  const [intp, fracp = '00'] = num.toFixed(2).split('.');
  let s = intp;
  if (s.length > 3) {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const groups = [];
    while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
    if (rest) groups.unshift(rest);
    s = groups.join(',') + ',' + last3;
  }
  return `${sign}\u20B9${s}.${fracp}`;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d.slice(0, 10); }
}

const EMPTY_FORM = {
  direction: 'credit',
  entry_type: '',
  amount: '',
  entry_date: new Date().toISOString().slice(0, 10),
  description: '',
  reference_number: '',
  custom_label: '',
  link_mode: 'unlinked', // unlinked | linked
  linked_invoice: null,
};

const CustomerAdjustments = ({ customerId, onChanged }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [tab, setTab] = useState('ledger'); // ledger | entries
  const [adjustments, setAdjustments] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState({ debit: [], credit: [] });
  const [openInvoices, setOpenInvoices] = useState([]);
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', form, original }
  const [auditOpen, setAuditOpen] = useState(null);
  const [voidConfirm, setVoidConfirm] = useState(null);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Send via WhatsApp
  const [waOpen, setWaOpen] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waForm, setWaForm] = useState({ phone: '', template_name: '', from_date: '', to_date: '' });
  const [waPreview, setWaPreview] = useState(null);
  const [waSent, setWaSent] = useState(null);
  // Opening Balances
  const [obData, setObData] = useState({ current_fy: null, opening_balances: [] });
  const [obDialog, setObDialog] = useState(null); // { fy_label, fy_start?, fy_end?, existing? } | null
  const [obSaving, setObSaving] = useState(false);
  const [obVoidConfirm, setObVoidConfirm] = useState(null);
  // Record Payment (FIFO) dialog — Payment Received entry type.
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, oi, ob] = await Promise.all([
        arApi.listAdjustments(customerId),
        arApi.adjustmentTypes(),
        arApi.customerOpenInvoices(customerId).catch(() => ({ data: [] })),
        arApi.listOpeningBalances(customerId).catch(() => ({ data: { current_fy: null, opening_balances: [] } })),
      ]);
      setAdjustments(a.data || []);
      setTypes(t.data || { debit: [], credit: [] });
      setOpenInvoices(oi.data || []);
      setObData(ob.data || { current_fy: null, opening_balances: [] });
    } catch (e) {
      toast.error('Failed to load adjustments');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const loadLedger = useCallback(async () => {
    const params = {};
    if (filterFrom) params.from_date = filterFrom;
    if (filterTo) params.to_date = filterTo;
    try {
      const r = await arApi.customerLedger(customerId, params);
      setLedger(r.data);
    } catch { /* silent */ }
  }, [customerId, filterFrom, filterTo]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadLedger(); }, [loadLedger, adjustments.length]);

  const openCreate = (direction = 'credit') => {
    setDialog({ mode: 'create', form: { ...EMPTY_FORM, direction } });
  };
  const openEdit = (adj) => {
    setDialog({
      mode: 'edit',
      original: adj,
      form: {
        direction: adj.direction,
        entry_type: adj.entry_type,
        amount: String(adj.amount),
        entry_date: adj.entry_date,
        description: adj.description || '',
        reference_number: adj.reference_number || '',
        custom_label: adj.custom_label || '',
        link_mode: adj.linked_invoice ? 'linked' : 'unlinked',
        linked_invoice: adj.linked_invoice || null,
        edit_reason: '',
      },
    });
  };

  const save = async () => {
    const f = dialog.form;
    if (!f.entry_type) return toast.error('Pick an entry type');
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) return toast.error('Amount must be > 0');
    if (!f.entry_date) return toast.error('Pick an entry date');
    if (f.link_mode === 'linked' && !f.linked_invoice) return toast.error('Pick an invoice or switch to standalone');
    if (f.entry_type.startsWith('other_') && !f.custom_label.trim()) return toast.error('Please specify a label for "Other"');

    const payload = {
      entry_type: f.entry_type,
      direction: f.direction,
      amount: amt,
      entry_date: f.entry_date,
      description: f.description.trim(),
      reference_number: f.reference_number.trim(),
      custom_label: f.entry_type.startsWith('other_') ? f.custom_label.trim() : '',
      linked_invoice: f.link_mode === 'linked' ? f.linked_invoice : null,
    };
    setSaving(true);
    try {
      if (dialog.mode === 'edit') {
        await arApi.updateAdjustment(customerId, dialog.original.id, { ...payload, edit_reason: f.edit_reason || '' });
        toast.success('Adjustment updated');
      } else {
        await arApi.createAdjustment(customerId, payload);
        toast.success('Adjustment recorded');
      }
      setDialog(null);
      loadAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doVoid = async () => {
    try {
      await arApi.voidAdjustment(customerId, voidConfirm.adj.id, voidConfirm.reason || '');
      toast.success('Adjustment voided');
      setVoidConfirm(null);
      loadAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Void failed');
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (filterFrom) params.from_date = filterFrom;
      if (filterTo) params.to_date = filterTo;
      const qs = new URLSearchParams(params).toString();
      const token = localStorage.getItem('siddhi_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ar/customers/${customerId}/ledger.pdf${qs ? '?' + qs : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ledger-${ledger?.company_name || 'customer'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  // ── Send Ledger via WhatsApp ──
  const openWhatsApp = async () => {
    setWaSent(null);
    setWaForm({ phone: '', template_name: '', from_date: filterFrom, to_date: filterTo });
    setWaOpen(true);
    try {
      const r = await arApi.previewLedgerDataPoints(customerId, {
        ...(filterFrom ? { from_date: filterFrom } : {}),
        ...(filterTo ? { to_date: filterTo } : {}),
      });
      setWaPreview(r.data);
      setWaForm(f => ({ ...f, phone: r.data.mobile || '' }));
    } catch {
      toast.error('Could not load preview');
    }
  };

  // Refresh preview if date range changes
  useEffect(() => {
    if (!waOpen) return;
    let cancelled = false;
    arApi.previewLedgerDataPoints(customerId, {
      ...(waForm.from_date ? { from_date: waForm.from_date } : {}),
      ...(waForm.to_date ? { to_date: waForm.to_date } : {}),
    }).then(r => { if (!cancelled) setWaPreview(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [waForm.from_date, waForm.to_date, waOpen, customerId]);

  const sendWhatsApp = async () => {
    if (!waForm.phone) return toast.error('Phone required');
    setWaSending(true);
    try {
      const r = await arApi.sendLedgerWhatsApp(customerId, {
        phone: waForm.phone,
        template_name: waForm.template_name || undefined,
        from_date: waForm.from_date || undefined,
        to_date: waForm.to_date || undefined,
      });
      setWaSent(r.data);
      toast.success(`Ledger sent to ${r.data.phone}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'WhatsApp send failed');
    } finally {
      setWaSending(false);
    }
  };

  const copyValue = (val) => {
    navigator.clipboard?.writeText(String(val)).then(() => toast.success('Copied'));
  };

  // ── Opening Balances ──
  const openOBDialog = (fy_label, existing = null) => {
    setObDialog({
      fy_label,
      existing,
      opening_balance: existing ? String(existing.opening_balance) : '',
      direction: existing?.direction || 'debit',
      notes: existing?.notes || '',
      edit_reason: '',
    });
  };
  const saveOB = async () => {
    const f = obDialog;
    const amt = parseFloat(f.opening_balance);
    if (isNaN(amt) || amt < 0) return toast.error('Amount must be ≥ 0');
    setObSaving(true);
    try {
      await arApi.upsertOpeningBalance(customerId, {
        fy_label: f.fy_label,
        opening_balance: amt,
        direction: f.direction,
        notes: f.notes.trim(),
        edit_reason: f.edit_reason || '',
      });
      toast.success(`Opening balance saved for ${f.fy_label}`);
      setObDialog(null);
      loadAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setObSaving(false);
    }
  };
  const voidOB = async () => {
    try {
      await arApi.voidOpeningBalance(customerId, obVoidConfirm.ob.id, obVoidConfirm.reason || '');
      toast.success(`Opening balance for ${obVoidConfirm.ob.fy_label} removed`);
      setObVoidConfirm(null);
      loadAll();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };
  // Total outstanding — used to gate the Payment Received button.
  const outstandingTotal = useMemo(
    () => (openInvoices || []).reduce((s, inv) => s + (parseFloat(inv.balance) || 0), 0),
    [openInvoices]
  );

  // Build the FY chips to display: current FY + previous 2 FYs
  const fyChips = useMemo(() => {
    if (!obData.current_fy) return [];
    const cur = obData.current_fy.label; // e.g. FY2026-27
    const startYear = parseInt(cur.replace('FY', '').split('-')[0], 10);
    const years = [startYear, startYear - 1, startYear - 2];
    return years.map(y => {
      const label = `FY${y}-${String(y + 1).slice(2)}`;
      const existing = obData.opening_balances.find(o => o.fy_label === label);
      return { label, existing };
    });
  }, [obData]);
  const typesForDirection = dialog ? (dialog.form.direction === 'debit' ? types.debit : types.credit) : [];

  return (
    <Card data-testid="customer-adjustments-card" className="overflow-hidden border-violet-200/60 dark:border-violet-500/30">
      <CardHeader className="pb-3 px-4 md:px-5 pt-4 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/10 border-b">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              Customer Ledger
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Invoices, payments, and manual adjustment entries — chronologically with running balance
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setReceivePaymentOpen(true)}
              disabled={outstandingTotal <= 0}
              title="Record a lump-sum payment received — auto-allocates FIFO across pending invoices"
              data-testid="record-payment-received-btn">
              <Receipt className="h-4 w-4 mr-1.5" /> Payment Received
            </Button>
            <Button size="sm" variant="outline" className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => openCreate('credit')} data-testid="add-credit-adjustment-btn">
              <ArrowDownCircle className="h-4 w-4 mr-1.5" /> Add Credit
            </Button>
            <Button size="sm" variant="outline" className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => openCreate('debit')} data-testid="add-debit-adjustment-btn">
              <ArrowUpCircle className="h-4 w-4 mr-1.5" /> Add Debit
            </Button>
            <Button size="sm" variant="outline" className="h-9 border-green-400 text-green-700 hover:bg-green-50 dark:text-green-300"
              onClick={openWhatsApp} data-testid="send-ledger-whatsapp-btn">
              <MessageCircle className="h-4 w-4 mr-1.5" /> Send via WhatsApp
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={downloading || !ledger}
              className="h-9 bg-violet-600 hover:bg-violet-700 text-white" data-testid="download-ledger-pdf-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
              PDF
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 mt-3">
          <button
            type="button"
            onClick={() => setTab('ledger')}
            className={cn('px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
              tab === 'ledger' ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:bg-violet-100 dark:hover:bg-violet-500/20')}
            data-testid="tab-ledger"
          >
            Ledger
          </button>
          <button
            type="button"
            onClick={() => setTab('entries')}
            className={cn('px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
              tab === 'entries' ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:bg-violet-100 dark:hover:bg-violet-500/20')}
            data-testid="tab-entries"
          >
            Manual entries ({adjustments.length})
          </button>
        </div>
      </CardHeader>

      <CardContent className="p-4 md:p-5 space-y-4">
        {/* Opening Balances strip — Indian FY context */}
        <div className="rounded-xl border bg-slate-50/60 dark:bg-neutral-800/30 p-3" data-testid="opening-balances-section">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Landmark className="h-4 w-4 text-amber-700 dark:text-amber-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                Opening Balances <span className="text-muted-foreground font-normal">(Indian FY)</span>
              </p>
            </div>
            {obData.current_fy && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Current: {obData.current_fy.label}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {fyChips.map(({ label, existing }) => {
              const isApplicable = obData.opening_balances.length > 0
                && obData.opening_balances[0].fy_label === label;
              return (
                <div
                  key={label}
                  className={cn('rounded-lg border p-2.5 flex items-center justify-between gap-2 transition-colors',
                    existing ? 'bg-white dark:bg-neutral-900 border-amber-200 dark:border-amber-500/30' : 'bg-slate-50 dark:bg-neutral-900/50 border-dashed')}
                  data-testid={`ob-${label}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs font-bold tabular-nums">{label}</p>
                      {isApplicable && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-300 text-amber-700">
                          Active
                        </Badge>
                      )}
                    </div>
                    {existing ? (
                      <p className={cn('text-sm font-semibold tabular-nums mt-0.5',
                        existing.direction === 'debit' ? 'text-rose-600' : 'text-emerald-600')}>
                        {existing.direction === 'debit' ? 'Dr ' : 'Cr '}{fmtINR(existing.opening_balance)}
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground mt-0.5">Not set</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => openOBDialog(label, existing)}
                      className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:underline px-1"
                      data-testid={`ob-edit-${label}`}
                    >
                      {existing ? 'Edit' : 'Set'}
                    </button>
                    {existing && (
                      <button
                        onClick={() => setObVoidConfirm({ ob: existing, reason: '' })}
                        className="text-[10px] text-rose-500 hover:underline px-1"
                        data-testid={`ob-void-${label}`}
                        aria-label="Remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {obData.opening_balances.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              The active OB starts the ledger on its FY's 1 April — invoices, payments &amp; adjustments dated before then are encapsulated in this figure and won't be counted twice.
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
        ) : tab === 'ledger' ? (
          <>
            {/* Date filter */}
            <div className="flex flex-wrap gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">From</Label>
                <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="h-9 text-sm w-44" data-testid="ledger-from-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">To</Label>
                <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="h-9 text-sm w-44" data-testid="ledger-to-date" />
              </div>
              {(filterFrom || filterTo) && (
                <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={() => { setFilterFrom(''); setFilterTo(''); }}>
                  Clear
                </Button>
              )}
              <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Filter className="h-3 w-3" /> {ledger?.events?.length || 0} transactions
              </div>
            </div>

            {/* Summary strip */}
            {ledger && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <SummaryTile label="Opening" value={fmtINR(ledger.opening_balance)} muted />
                <SummaryTile label="Total debits" value={fmtINR(ledger.total_debit)} accent="text-rose-600" />
                <SummaryTile label="Total credits" value={fmtINR(ledger.total_credit)} accent="text-emerald-600" />
                <SummaryTile label="Closing" value={fmtINR(ledger.closing_balance)}
                  accent={ledger.closing_balance > 0 ? 'text-rose-600' : 'text-emerald-600'} strong />
              </div>
            )}

            {/* Transactions */}
            <div className="border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm" data-testid="ledger-table">
                  <thead className="bg-slate-50 dark:bg-neutral-800/50 text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Particulars</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] hidden sm:table-cell">Ref</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Debit</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Credit</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!ledger || ledger.events.length === 0) ? (
                      <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No transactions in this period.</td></tr>
                    ) : ledger.events.map((ev, i) => (
                      <tr key={`${ev.adjustment_id || ev.type}-${i}`} className="border-t hover:bg-slate-50/60 dark:hover:bg-neutral-800/40">
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px]">{fmtDate(ev.date)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{ev.label}</div>
                          {ev.description && <div className="text-[10px] text-muted-foreground italic mt-0.5">{ev.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground hidden sm:table-cell">{ev.ref || ev.co_number || '—'}</td>
                        <td className={cn("px-3 py-2 text-right tabular-nums whitespace-nowrap", ev.debit > 0 && "text-rose-600 font-semibold")}>
                          {ev.debit > 0 ? fmtINR(ev.debit) : ''}
                        </td>
                        <td className={cn("px-3 py-2 text-right tabular-nums whitespace-nowrap", ev.credit > 0 && "text-emerald-600 font-semibold")}>
                          {ev.credit > 0 ? fmtINR(ev.credit) : ''}
                        </td>
                        <td className={cn("px-3 py-2 text-right tabular-nums font-bold whitespace-nowrap",
                          ev.balance > 0 ? "text-slate-900 dark:text-slate-100" : "text-emerald-700 dark:text-emerald-400")}>
                          {fmtINR(ev.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          // Entries tab
          <>
            {adjustments.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-xl">
                <p className="text-sm text-muted-foreground">No manual adjustments recorded yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Use <strong>Add Credit</strong> or <strong>Add Debit</strong> above.</p>
              </div>
            ) : (
              <div className="divide-y border rounded-xl overflow-hidden" data-testid="adjustments-list">
                {adjustments.map(adj => (
                  <div key={adj.id} className="p-3 flex items-start gap-3 hover:bg-slate-50/60 dark:hover:bg-neutral-800/30" data-testid={`adjustment-${adj.id}`}>
                    <div className={cn('h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0',
                      adj.direction === 'debit' ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-emerald-100 dark:bg-emerald-500/20')}>
                      {adj.direction === 'debit'
                        ? <ArrowUpCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        : <ArrowDownCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{adj.label}</span>
                        {adj.linked_invoice ? (
                          <Badge variant="outline" className="text-[10px] gap-0.5">
                            <Link2 className="h-2.5 w-2.5" /> {adj.linked_invoice.invoice_number || 'invoice'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] gap-0.5 text-muted-foreground">
                            <Unlink className="h-2.5 w-2.5" /> Standalone
                          </Badge>
                        )}
                        {(adj.audit_trail?.length || 0) > 1 && (
                          <button onClick={() => setAuditOpen(adj)} className="text-[10px] flex items-center gap-0.5 text-violet-600 hover:underline" data-testid={`audit-${adj.id}`}>
                            <History className="h-2.5 w-2.5" /> {adj.audit_trail.length - 1} edit{adj.audit_trail.length - 1 === 1 ? '' : 's'}
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                        <span className="font-mono">{fmtDate(adj.entry_date)}</span>
                        {adj.reference_number && <span>Ref: {adj.reference_number}</span>}
                        <span>by {adj.created_by}</span>
                      </div>
                      {adj.description && <p className="text-[12px] mt-1 text-slate-700 dark:text-slate-300">{adj.description}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className={cn('text-base font-bold tabular-nums',
                        adj.direction === 'debit' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400')}>
                        {adj.direction === 'debit' ? '+' : '-'} {fmtINR(adj.amount)}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => openEdit(adj)} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted" data-testid={`edit-adjustment-${adj.id}`} aria-label="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {isSuperAdmin && (
                          <button onClick={() => setVoidConfirm({ adj, reason: '' })} className="h-7 w-7 rounded-md flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" data-testid={`void-adjustment-${adj.id}`} aria-label="Void" title="Delete / void (super_admin only)">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Record Payment (FIFO) dialog — Payment Received entry type. */}
      <ReceivePaymentDialog
        open={receivePaymentOpen}
        customerId={customerId}
        outstanding={outstandingTotal}
        openInvoices={openInvoices || []}
        onClose={() => setReceivePaymentOpen(false)}
        onRecorded={() => { loadAll(); onChanged?.(); }}
      />

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === 'edit' ? 'Edit adjustment' : `New ${dialog?.form.direction === 'debit' ? 'debit' : 'credit'} entry`}
            </DialogTitle>
            <DialogDescription>
              {dialog?.form.direction === 'debit'
                ? 'Increases customer outstanding (e.g., penalty, additional charges).'
                : 'Reduces customer outstanding (e.g., credit note, advance received).'}
            </DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3.5">
              {/* Direction toggle */}
              <div className="flex gap-2">
                {['credit', 'debit'].map(dir => (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => setDialog(d => ({ ...d, form: { ...d.form, direction: dir, entry_type: '' } }))}
                    className={cn('flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-colors',
                      dialog.form.direction === dir
                        ? (dir === 'debit' ? 'bg-rose-50 border-rose-400 text-rose-700' : 'bg-emerald-50 border-emerald-400 text-emerald-700')
                        : 'border-slate-200 text-muted-foreground hover:border-slate-300')}
                    data-testid={`direction-${dir}`}
                  >
                    {dir === 'debit' ? '↑ Debit (increases due)' : '↓ Credit (reduces due)'}
                  </button>
                ))}
              </div>

              {/* Entry type */}
              <div className="space-y-1.5">
                <Label className="text-sm">Entry type *</Label>
                <Select value={dialog.form.entry_type} onValueChange={(v) => setDialog(d => ({ ...d, form: { ...d.form, entry_type: v } }))}>
                  <SelectTrigger className="h-10 text-sm" data-testid="entry-type-select"><SelectValue placeholder="Pick a type..." /></SelectTrigger>
                  <SelectContent>
                    {typesForDirection.map(t => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Custom label for "Other" */}
              {(dialog.form.entry_type === 'other_debit' || dialog.form.entry_type === 'other_credit') && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Specify *</Label>
                  <Input value={dialog.form.custom_label}
                    onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, custom_label: e.target.value } }))}
                    placeholder="e.g., FX conversion fee" className="h-10 text-sm" data-testid="custom-label-input" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Amount (₹) *</Label>
                  <Input type="number" min="0" step="0.01" value={dialog.form.amount}
                    onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, amount: e.target.value } }))}
                    placeholder="0.00" className="h-10 text-sm font-mono" data-testid="amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Entry date *</Label>
                  <Input type="date" value={dialog.form.entry_date}
                    onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, entry_date: e.target.value } }))}
                    className="h-10 text-sm" data-testid="entry-date-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Reference number</Label>
                <Input value={dialog.form.reference_number}
                  onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, reference_number: e.target.value } }))}
                  placeholder="CN-001, NEFT-XYZ, GST-CN..." className="h-10 text-sm" data-testid="reference-input" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Description</Label>
                <Textarea value={dialog.form.description}
                  onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, description: e.target.value } }))}
                  placeholder="Optional notes..." rows={2} className="text-sm" data-testid="description-input" />
              </div>

              {/* Link to invoice */}
              <div className="rounded-lg border p-3 space-y-2 bg-slate-50/60 dark:bg-neutral-800/30">
                <Label className="text-sm font-semibold">Allocate to an invoice?</Label>
                <div className="flex gap-2">
                  {['unlinked', 'linked'].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDialog(d => ({ ...d, form: { ...d.form, link_mode: m, linked_invoice: m === 'unlinked' ? null : d.form.linked_invoice } }))}
                      className={cn('flex-1 px-3 py-2 rounded-md text-xs font-medium border transition-colors',
                        dialog.form.link_mode === m
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 dark:bg-neutral-900 dark:text-slate-200')}
                      data-testid={`link-mode-${m}`}
                    >
                      {m === 'unlinked' ? 'Standalone (customer-level)' : 'Link to specific invoice'}
                    </button>
                  ))}
                </div>
                {dialog.form.link_mode === 'linked' && (
                  <div className="space-y-1.5 pt-1">
                    <Label className="text-xs text-muted-foreground">Pick an open invoice (oldest first)</Label>
                    <Select
                      value={dialog.form.linked_invoice ? `${dialog.form.linked_invoice.order_id}::${dialog.form.linked_invoice.dispatch_id}` : ''}
                      onValueChange={(v) => {
                        const [oid, did] = v.split('::');
                        const inv = openInvoices.find(i => i.order_id === oid && i.dispatch_id === did);
                        setDialog(d => ({ ...d, form: { ...d.form, linked_invoice: inv ? { order_id: oid, dispatch_id: did, invoice_number: inv.invoice_number } : null } }));
                      }}
                    >
                      <SelectTrigger className="h-10 text-sm" data-testid="link-invoice-select">
                        <SelectValue placeholder={openInvoices.length === 0 ? 'No open invoices' : 'Select invoice...'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {openInvoices.map(inv => (
                          <SelectItem key={`${inv.order_id}::${inv.dispatch_id}`} value={`${inv.order_id}::${inv.dispatch_id}`}>
                            <div className="flex flex-col gap-0.5">
                              <span className="font-semibold text-sm">{inv.invoice_number || inv.co_number}</span>
                              <span className="text-[10px] text-muted-foreground">
                                {fmtDate(inv.invoice_date)} · Bal {fmtINR(inv.balance)}
                                {inv.days_overdue > 0 && <span className="text-rose-600 ml-1">· {inv.days_overdue}d overdue</span>}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {dialog.mode === 'edit' && (
                <div className="space-y-1.5 pt-1 border-t">
                  <Label className="text-sm flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                    Reason for edit (saved to audit trail)
                  </Label>
                  <Input value={dialog.form.edit_reason || ''}
                    onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, edit_reason: e.target.value } }))}
                    placeholder="e.g., Corrected amount as per CN-XYZ" className="h-10 text-sm" data-testid="edit-reason-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-adjustment-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {dialog?.mode === 'edit' ? 'Save changes' : 'Add entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void confirm */}
      <Dialog open={!!voidConfirm} onOpenChange={(o) => { if (!o) setVoidConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Void this adjustment?</DialogTitle>
            <DialogDescription>
              This won't be deleted — it stays in the audit trail as voided.
            </DialogDescription>
          </DialogHeader>
          {voidConfirm && (
            <div className="space-y-2">
              <div className="rounded-lg bg-slate-50 dark:bg-neutral-800/50 p-3 text-sm">
                <p className="font-semibold">{voidConfirm.adj.label}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtDate(voidConfirm.adj.entry_date)} · {fmtINR(voidConfirm.adj.amount)} · {voidConfirm.adj.direction}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason (optional)</Label>
                <Input value={voidConfirm.reason}
                  onChange={(e) => setVoidConfirm(v => ({ ...v, reason: e.target.value }))}
                  placeholder="e.g., Duplicate entry" className="h-9 text-sm" data-testid="void-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidConfirm(null)}>Cancel</Button>
            <Button onClick={doVoid} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-void-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit trail */}
      <Dialog open={!!auditOpen} onOpenChange={(o) => { if (!o) setAuditOpen(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Audit trail</DialogTitle>
            <DialogDescription>{auditOpen?.label} · {auditOpen ? fmtINR(auditOpen.amount) : ''}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {(auditOpen?.audit_trail || []).slice().reverse().map((e, i) => (
              <div key={i} className="border-l-2 border-violet-300 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge variant="outline" className="capitalize text-[10px]">{e.action}</Badge>
                  <span className="font-semibold">{e.by}</span>
                  <span className="text-muted-foreground">· {fmtDate(e.at)}</span>
                </div>
                {e.reason && <p className="text-xs text-muted-foreground mt-1 italic">"{e.reason}"</p>}
                {e.action === 'updated' && e.before && e.after && (
                  <div className="mt-1.5 grid grid-cols-2 gap-2 text-[11px]">
                    {Object.keys(e.before).filter(k => JSON.stringify(e.before[k]) !== JSON.stringify(e.after[k])).map(k => (
                      <React.Fragment key={k}>
                        <div className="bg-rose-50 dark:bg-rose-500/10 p-1.5 rounded text-rose-700 dark:text-rose-300">
                          <p className="text-[9px] uppercase font-bold">Before {k}</p>
                          <p className="break-all">{JSON.stringify(e.before[k]) || '—'}</p>
                        </div>
                        <div className="bg-emerald-50 dark:bg-emerald-500/10 p-1.5 rounded text-emerald-700 dark:text-emerald-300">
                          <p className="text-[9px] uppercase font-bold">After {k}</p>
                          <p className="break-all">{JSON.stringify(e.after[k]) || '—'}</p>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Send Ledger via WhatsApp dialog */}
      <Dialog open={waOpen} onOpenChange={(o) => { if (!o) { setWaOpen(false); setWaSent(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" /> Send ledger via WhatsApp
            </DialogTitle>
            <DialogDescription>
              We'll send a DoubleTick template with the PDF attached and the values mapped to body placeholders. Preview every data point below before sending.
            </DialogDescription>
          </DialogHeader>

          {waSent ? (
            <div className="space-y-3 py-2">
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <p className="font-semibold text-emerald-900 dark:text-emerald-200">
                    Sent to {waSent.phone} via "{waSent.template_name}"
                  </p>
                </div>
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  Document: <code className="bg-white/60 dark:bg-black/30 px-1.5 py-0.5 rounded">{waSent.filename}</code>
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                If your DoubleTick template doesn't yet exist or has different variables, you can copy any of the values below.
              </div>
              <DataPointsTable points={waPreview} onCopy={copyValue} />
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setWaSent(null); setWaOpen(false); }}>Close</Button>
                <Button onClick={() => setWaSent(null)} variant="outline">Send another</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Send config */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Recipient phone</Label>
                  <Input value={waForm.phone}
                    onChange={(e) => setWaForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="919XXXXXXXXX" className="h-10 font-mono text-sm" data-testid="wa-phone-input" />
                  <p className="text-[10px] text-muted-foreground">Defaults to the customer's primary mobile.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">DoubleTick template name</Label>
                  <Input value={waForm.template_name}
                    onChange={(e) => setWaForm(f => ({ ...f, template_name: e.target.value }))}
                    placeholder="siddhi_ledger_statement (default)" className="h-10 text-sm" data-testid="wa-template-input" />
                  <p className="text-[10px] text-muted-foreground">Must be an APPROVED template at DoubleTick with header=DOCUMENT and 6 body variables.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Period — From</Label>
                  <Input type="date" value={waForm.from_date}
                    onChange={(e) => setWaForm(f => ({ ...f, from_date: e.target.value }))}
                    className="h-10 text-sm" data-testid="wa-from-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Period — To</Label>
                  <Input type="date" value={waForm.to_date}
                    onChange={(e) => setWaForm(f => ({ ...f, to_date: e.target.value }))}
                    className="h-10 text-sm" data-testid="wa-to-input" />
                </div>
              </div>

              {/* Live preview of all data points */}
              <div className="rounded-xl border bg-slate-50/60 dark:bg-neutral-800/30 p-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Data points DoubleTick will receive
                </p>
                <DataPointsTable points={waPreview} onCopy={copyValue} />
              </div>
            </div>
          )}

          {!waSent && (
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setWaOpen(false)} disabled={waSending}>Cancel</Button>
              <Button onClick={sendWhatsApp} disabled={waSending || !waForm.phone || !waPreview}
                className="bg-green-600 hover:bg-green-700 text-white" data-testid="wa-confirm-send-btn">
                {waSending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                Send now
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {/* Opening Balance dialog */}
      <Dialog open={!!obDialog} onOpenChange={(o) => { if (!o) setObDialog(null); }}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-amber-700" />
              {obDialog?.existing ? 'Edit' : 'Set'} Opening Balance — {obDialog?.fy_label}
            </DialogTitle>
            <DialogDescription>
              This is the customer's balance carried over on <strong>1 April</strong> of {obDialog?.fy_label}. Once set, all transactions dated before that date are considered already included in this figure.
            </DialogDescription>
          </DialogHeader>
          {obDialog && (
            <div className="space-y-3.5">
              <div className="flex gap-2">
                {['debit', 'credit'].map(dir => (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => setObDialog(d => ({ ...d, direction: dir }))}
                    className={cn('flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-colors',
                      obDialog.direction === dir
                        ? (dir === 'debit' ? 'bg-rose-50 border-rose-400 text-rose-700' : 'bg-emerald-50 border-emerald-400 text-emerald-700')
                        : 'border-slate-200 text-muted-foreground hover:border-slate-300')}
                    data-testid={`ob-direction-${dir}`}
                  >
                    {dir === 'debit' ? 'Debit (customer owes us)' : 'Credit (we owe customer)'}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Amount (₹) *</Label>
                <Input type="number" min="0" step="0.01" value={obDialog.opening_balance}
                  onChange={(e) => setObDialog(d => ({ ...d, opening_balance: e.target.value }))}
                  placeholder="0.00" className="h-11 text-base font-mono" data-testid="ob-amount-input" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm">Notes</Label>
                <Textarea value={obDialog.notes}
                  onChange={(e) => setObDialog(d => ({ ...d, notes: e.target.value }))}
                  placeholder="e.g. Carried over from Tally as on 31-Mar-2025" rows={2} className="text-sm"
                  data-testid="ob-notes-input" />
              </div>

              {obDialog.existing && (
                <div className="space-y-1.5 pt-1 border-t">
                  <Label className="text-sm flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                    Reason for edit (audit trail)
                  </Label>
                  <Input value={obDialog.edit_reason}
                    onChange={(e) => setObDialog(d => ({ ...d, edit_reason: e.target.value }))}
                    placeholder="Why are you changing this?" className="h-9 text-sm"
                    data-testid="ob-edit-reason-input" />
                </div>
              )}

              <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 p-2.5 text-[11px] text-amber-900 dark:text-amber-300">
                <strong>Sets the starting point on:</strong> 01 Apr {obDialog.fy_label.replace('FY', '').split('-')[0]}.
                Everything in this app dated before that day will be hidden from this customer's ledger.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setObDialog(null)}>Cancel</Button>
            <Button onClick={saveOB} disabled={obSaving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ob-save-btn">
              {obSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OB Void confirm */}
      <Dialog open={!!obVoidConfirm} onOpenChange={(o) => { if (!o) setObVoidConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this opening balance?</DialogTitle>
            <DialogDescription>
              The ledger will revert to counting every historical event for this customer.
            </DialogDescription>
          </DialogHeader>
          {obVoidConfirm && (
            <div className="space-y-2">
              <div className="rounded-lg bg-slate-50 dark:bg-neutral-800/50 p-3 text-sm">
                <p className="font-semibold">{obVoidConfirm.ob.fy_label}</p>
                <p className="text-xs text-muted-foreground">
                  {obVoidConfirm.ob.direction === 'debit' ? 'Dr ' : 'Cr '}{fmtINR(obVoidConfirm.ob.opening_balance)}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason (optional)</Label>
                <Input value={obVoidConfirm.reason}
                  onChange={(e) => setObVoidConfirm(v => ({ ...v, reason: e.target.value }))}
                  placeholder="e.g. Wrong figure entered" className="h-9 text-sm" data-testid="ob-void-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setObVoidConfirm(null)}>Cancel</Button>
            <Button onClick={voidOB} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="ob-confirm-void-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
const DataPointsTable = ({ points, onCopy }) => {
  if (!points) return <div className="text-xs text-muted-foreground py-4">Loading preview...</div>;
  const rows = [
    { var: '{{1}} / {{customer_name}}', value: points.customer_name },
    { var: '{{2}} / {{closing_balance}}', value: points.closing_balance_text },
    { var: '{{3}} / {{total_debit}}', value: points.total_debit_text },
    { var: '{{4}} / {{total_credit}}', value: points.total_credit_text },
    { var: '{{5}} / {{period}}', value: points.period_text },
    { var: '{{6}} / {{statement_date}}', value: points.statement_date },
  ];
  const extras = [
    { label: 'Contact name', value: points.contact_name },
    { label: 'Mobile', value: points.mobile },
    { label: 'Opening balance', value: points.opening_balance_text },
    { label: 'Open invoices', value: points.open_invoices_count },
    { label: 'Overdue invoices', value: points.overdue_invoices_count },
    { label: 'Overdue amount', value: points.overdue_amount_text },
    { label: 'Oldest overdue', value: `${points.oldest_overdue_days} day(s)` },
    { label: 'PDF URL', value: points.pdf_url, mono: true },
    { label: 'Portal URL', value: points.portal_url, mono: true },
  ];
  return (
    <div className="space-y-3">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 dark:bg-neutral-800 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Template variable</th>
              <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Sent value</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t" data-testid={`wa-placeholder-row-${i + 1}`}>
                <td className="px-3 py-2 font-mono text-[11px] text-violet-700 dark:text-violet-300">{r.var}</td>
                <td className="px-3 py-2 font-semibold tabular-nums">{r.value || '—'}</td>
                <td className="px-3 py-2">
                  <button onClick={() => onCopy(r.value)} className="h-7 w-7 rounded hover:bg-muted flex items-center justify-center" aria-label="Copy">
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Extra fields available (for richer templates or webhooks)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {extras.map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[11px] bg-white dark:bg-neutral-900 border rounded-md px-2 py-1.5">
              <span className="text-muted-foreground flex-shrink-0">{e.label}</span>
              <span className={cn('font-medium truncate', e.mono && 'font-mono text-[10px]')} title={String(e.value || '')}>{e.value || '—'}</span>
              {e.value && (
                <button onClick={() => onCopy(e.value)} className="text-muted-foreground hover:text-foreground flex-shrink-0" aria-label="Copy">
                  <Copy className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        Body placeholders are sent in the exact order shown above. The PDF is attached automatically as the template's header document. Map these in your DoubleTick template editor.
      </p>
    </div>
  );
};

const SummaryTile = ({ label, value, accent = '', muted = false, strong = false }) => (
  <div className={cn('p-3 rounded-lg border', muted ? 'bg-slate-50 dark:bg-neutral-800/40' : 'bg-white dark:bg-neutral-900')}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
    <p className={cn('text-base md:text-lg tabular-nums mt-0.5', strong ? 'font-bold' : 'font-semibold', accent)}>{value}</p>
  </div>
);

export default CustomerAdjustments;
